import { useCompany } from "../../core";
import { getProjectNamesToSync, syncDevicesData } from "../integrations/energy-devices/energy-device-oems.client";
import styled from 'styled-components'; 
import { useEffect, useState } from 'react';
import { 
  Button,
  Container, 
  Icon, 
  Loader,
  Message,
} from 'semantic-ui-react';
import { LoadingBlock, PageLoading } from '../../ui/Loading';
import { ICP4ProjectUploads } from "./ICP4ProjectUploads";
import { createAmProject } from "../projects/amProject.client";
import { Toast } from "../../ui/Toast";
import { getAmProjectConfig } from "../../ps-models";
import { clearLineItemsStoreCache } from "../../lineitems-store/RestLineItemsStore.persistence";


// Premium styled components
const PageContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  min-height: 100vh;
  padding: 2rem 0;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  .main-title {
    font-size: 2.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #6435C9 0%, #480971 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1rem;
  }
  
  .subtitle {
    font-size: 1.1rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ProjectCard = styled.div<{ isCreated?: boolean; isAvailable?: boolean; isClosing?: boolean; isCompleted?: boolean }>`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid ${props => {
    if (props.isCompleted) return '#10b981';
    if (props.isCreated) return '#10b981';
    if (props.isClosing) return '#f59e0b';
    return 'transparent';
  }};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: default;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  opacity: ${props => props.isClosing ? 0.7 : 1};
  transform: ${props => props.isClosing ? 'scale(0.95)' : 'scale(1)'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => {
      if (props.isCompleted) return '#10b981';
      if (props.isCreated) return '#10b981';
      if (props.isClosing) return '#f59e0b';
      return '#6435C9';
    }};
    opacity: ${props => props.isCreated || props.isCompleted ? '1' : '0.3'};
  }
  
  &:hover {
    transform: ${props => props.isClosing ? 'scale(0.95)' : 'translateY(-8px)'};
    box-shadow: ${props => props.isClosing ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 20px 40px rgba(0, 0, 0, 0.12)'};
  }
  
  ${props => props.isCompleted && `
    background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
    border-color: #10b981;
  `}
  
  ${props => props.isClosing && `
    background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
    border-color: #f59e0b;
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: #e2e8f0;
    color: #475569;
    transform: scale(1.1);
  }
`;

const UndoButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: linear-gradient(135deg, #d97706, #b45309);
    transform: translateY(-1px);
  }
`;

const SuccessOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  color: white;
  z-index: 15;
  animation: fadeIn 0.5s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .success-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    animation: bounce 0.6s ease-in-out;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
  
  .success-message {
    font-size: 1.25rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 0.5rem;
  }
  
  .success-subtitle {
    font-size: 0.875rem;
    opacity: 0.9;
    text-align: center;
  }
`;

const ProjectHeader = styled.div<{}>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: auto;
  padding-top: '0';
  flex: 1;
`;

const ProjectInfo = styled.div`
  flex: 1;
  
  .project-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
`;

const StatItem = styled.div`
  text-align: center;
  
  .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #6435C9;
    margin-bottom: 0.25rem;
  }
  
  .label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger'; color?: string }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' && `
    background: ${props.color || '#6435C9'};
    color: white;
    
    &:hover {
      background: ${props.color ? props.color + 'dd' : '#5b21b6'};
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(100, 53, 201, 0.3);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e2e8f0;
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.variant === 'danger' && `
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    
    &:hover {
      background: #fee2e2;
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const UploadSection = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  .upload-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  z-index: 10;
`;

const UploadSidePanel = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-700px'};
  width: 700px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100vw;
    right: ${props => props.isOpen ? '0' : '-100vw'};
  }
`;

const SidePanelHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  
  .header-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }
  
  .header-subtitle {
    font-size: 0.875rem;
    color: #64748b;
  }
`;

const SidePanelContent = styled.div`
  flex: 1;
  padding: 0.2rem;
  overflow-y: auto;
`;

const ClosePanelButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e2e8f0;
    color: #475569;
    transform: scale(1.1);
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 999;
  pointer-events: none;
`;

interface ProjectState {
  name: string;
  isCreated: boolean;
  projectId?: string;
  isLoading: boolean;
  isClosing?: boolean;
  isCompleted?: boolean;
  closeTimeout?: NodeJS.Timeout;
}

interface UploadPanelState {
  isOpen: boolean;
  projectName?: string;
  projectId?: string;
}

export function ICP4ProjectSyncing({renderHeader = true}: {renderHeader?: boolean}) {
  const company = useCompany();
  const companyId = company.id;
  const {collection, namespace} = getAmProjectConfig(company);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsSyncing, setProjectsSyncing] = useState(false);
  const [projectStates, setProjectStates] = useState<ProjectState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadPanel, setUploadPanel] = useState<UploadPanelState>({
    isOpen: false
  });

  useEffect(() => {
    loadProjectsToSync();
  }, [companyId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      projectStates.forEach(project => {
        if (project.closeTimeout) {
          clearTimeout(project.closeTimeout);
        }
      });
    };
  }, [projectStates]);

  // Prevent body scroll when upload panel is open
  useEffect(() => {
    if (uploadPanel.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [uploadPanel.isOpen]);

  const loadProjectsToSync = async () => {
    setProjectsLoading(true);
    setError(null);
    
    try {
      const projectsToSync = await getProjectNamesToSync(companyId);
      
      // Initialize project states
      setProjectStates(projectsToSync.map(name => ({
        name,
        isCreated: false,
        isLoading: false
      })));
    } catch (err) {
      setError('Failed to load linked projects');
      console.error('Error loading projects to sync:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleCreateProject = async (projectName: string) => {
    setProjectStates(prev => prev.map(project => 
      project.name === projectName 
        ? { ...project, isLoading: true }
        : project
    ));
    setError(null);
    
    try {
      const project = await createAmProject(companyId, { projectName });
      
      // Update project state with the created project ID
      setProjectStates(prev => prev.map(p => 
        p.name === projectName 
          ? { 
              ...p, 
              isCreated: true, 
              projectId: project.id, 
              isLoading: false 
            }
          : p
      ));
      openUploadPanel(projectName, project.id);
    } catch (err) {
      setError(`Failed to create project ${projectName}`);
      console.error('Error creating project:', err);
      
      // Reset loading state on error
      setProjectStates(prev => prev.map(project => 
        project.name === projectName 
          ? { ...project, isLoading: false }
          : project
      ));
    }
  };

  const onClose = (projectName: string, closeReason: 'project-sync-complete' | 'user-closure') => {
    if (closeReason === 'user-closure') {
      // Start grace period for user closure
      setProjectStates(prev => prev.map(project => 
        project.name === projectName 
          ? { ...project, isClosing: true }
          : project
      ));

      // Set timeout to remove the card after grace period
      const timeout = setTimeout(() => {
        setProjectStates(prev => prev.filter(p => p.name !== projectName));
      }, 5000); // 5 second grace period

      // Store timeout reference
      setProjectStates(prev => prev.map(project => 
        project.name === projectName 
          ? { ...project, closeTimeout: timeout }
          : project
      ));
    } else if (closeReason === 'project-sync-complete') {
      // Show success state
      setProjectStates(prev => prev.map(project => 
        project.name === projectName 
          ? { ...project, isCompleted: true }
          : project
      ));

      // Remove card after showing success for a while
      const timeout = setTimeout(() => {
        setProjectStates(prev => prev.filter(p => p.name !== projectName));
      }, 3000); // 3 second success display

      // Store timeout reference
      setProjectStates(prev => prev.map(project => 
        project.name === projectName 
          ? { ...project, closeTimeout: timeout }
          : project
      ));
    }
  };

  const handleUndo = (projectName: string) => {
    // Clear the timeout and restore the card
    setProjectStates(prev => prev.map(project => {
      if (project.name === projectName) {
        if (project.closeTimeout) {
          clearTimeout(project.closeTimeout);
        }
        return { 
          ...project, 
          isClosing: false, 
          closeTimeout: undefined 
        };
      }
      return project;
    }));
  };

  const openUploadPanel = (projectName: string, projectId: string) => {
    setUploadPanel({
      isOpen: true,
      projectName,
      projectId
    });
  };

  const closeUploadPanel = () => {
    setUploadPanel({
      isOpen: false
    });
  };

  const handleProjectUploadComplete = (projectName: string) => {
    closeUploadPanel();
    onClose(projectName, 'project-sync-complete');
  };

  const handleProjectSync = () => {
    setError(null);
    setProjectsSyncing(true);
    syncDevicesData(companyId, collection).then(()=>{
      clearLineItemsStoreCache();
    }).catch((err)=>{
      console.error("Failed to sync projects", err)
      setError('Failed to sync projects');
    }).finally(()=>{
      setProjectsSyncing(false);
    })
  }

  if (projectsLoading) {
    return <LoadingBlock />;
  }

  if(projectsSyncing){
    return <PageLoading />;
  }

  return (<>
      <PageContainer>
        <Container fluid>
        {renderHeader && <div
        style={{
          textAlign: 'right',
          marginBottom: '3rem',
        }}
        ><Button
                      primary
                      onClick={handleProjectSync}
                      style={{
                        background: '#6435C9',
                        color: '#FFFFFF',
                        // @TODO: Evaluate...
                        opacity: true ? 1 : 0.6,
                        borderRadius: '8px',
                        padding: '12px 24px',
                        height: '40px',
                        fontWeight: 500,
                      }}
                    >
                      Sync Data
                    </Button></div>}
          {/* <h1 className="main-title">Energy Device Integrations</h1> */}
          {/* <p className="subtitle"> */}
            {/* Connect your solar inverters to automatically sync data and monitor performance. */}
          {/* </p> */}
        {/* </HeaderSection>} */}
          
          <Toast onClose={() => setError(null)} severity="error" content={error} />

          {projectStates.length === 0 && !projectsLoading && (
            <Message info style={{ borderRadius: '12px', marginBottom: '2rem', textAlign: 'center' }}>
              <Message.Header>No Sites Found to Sync</Message.Header>
              <p>
              Either all sites have been configured or there are no sites available for sync. Please use the navigation buttons above to navigate in your onboarding journey.
              </p>
            </Message>
          )}

          <ProjectGrid>
            {projectStates.map((projectState) => {
              const inLoadingState = projectState.isLoading || projectsLoading;

              return (
                <ProjectCard
                  key={projectState.name}
                  isCreated={projectState.isCreated}
                  isClosing={projectState.isClosing}
                  isCompleted={projectState.isCompleted}
                >
                  {inLoadingState && (
                    <LoadingOverlay>
                      <Loader size="small" active inline />
                    </LoadingOverlay>
                  )}

                  {projectState.isCompleted && (
                    <SuccessOverlay>
                      <div className="success-icon">
                        <Icon name="check circle" />
                      </div>
                      <div className="success-message">
                        {projectState.name} Configured
                      </div>
                      <div className="success-subtitle">
                        Project setup completed successfully
                      </div>
                    </SuccessOverlay>
                  )}

                  {!projectState.projectId && !projectState.isCompleted && !projectState.isClosing && (
                    <CloseButton
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onClose(projectState.name, 'user-closure');
                      }}
                      title="Remove project"
                    >
                      <Icon name="close" />
                    </CloseButton>
                  )}

                  {projectState.isClosing && (
                    <UndoButton
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleUndo(projectState.name);
                      }}
                    >
                      <Icon name="undo" />
                      Undo
                    </UndoButton>
                  )}

                  <ProjectHeader>
                    <ProjectInfo>
                      <div className="project-name">{projectState.name}</div>
                    </ProjectInfo>
                  </ProjectHeader>
                  
                  <ActionButtons>
                    {!projectState.isCreated && (
                      <ActionButton
                        variant="primary"
                        disabled={inLoadingState || projectState.isClosing}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleCreateProject(projectState.name);
                        }}
                      >
                        <Icon name="plus" />
                        Configure Project
                      </ActionButton>
                    )}
                    
                    {/* {projectState.isCreated && projectState.projectId && !projectState.isCompleted && (
                      <ActionButton
                        variant="secondary"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          openUploadPanel(projectState.name, projectState.projectId!);
                        }}
                      >
                        <Icon name="upload" />
                        Upload Documents
                      </ActionButton>
                    )} */}
                  </ActionButtons>
                </ProjectCard>
              );
            })}
          </ProjectGrid>
        </Container>
      </PageContainer>

      {/* Upload Side Panel */}
      <Overlay isOpen={uploadPanel.isOpen} />
      <UploadSidePanel isOpen={uploadPanel.isOpen}>
        <SidePanelHeader>
          {namespace === "ICP4SEE" && <ClosePanelButton onClick={()=> handleProjectUploadComplete(uploadPanel.projectName!)}>
            <Icon name="close" />
          </ClosePanelButton>}
          <div className="header-title">Upload Documents</div>
          <div className="header-subtitle">{uploadPanel.projectName}</div>
        </SidePanelHeader>
        <SidePanelContent>
          {uploadPanel.projectId && (
            <ICP4ProjectUploads 
              projectId={uploadPanel.projectId} 
              onProcessCompletion={() => handleProjectUploadComplete(uploadPanel.projectName!)}
            />
          )}
        </SidePanelContent>
      </UploadSidePanel>
      </>
  );
}